import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, InputAdornment,
  CircularProgress, Avatar, Tooltip, TablePagination, Button,
  FormControl, InputLabel, Select, MenuItem, Tabs, Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminListarDocumentos } from '../../services/adminApi';
import { adminObterPdf, getAdminToken } from '../../services/adminApi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const getToken = () => getAdminToken() || '';
const fmt = (d: string) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const fmtSize = (b: number) =>
  b ? (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${(b / 1024).toFixed(1)} KB`) : '—';

async function obterPdfBase64(documentoId: number, servidorId: number): Promise<string> {
  const data = await adminObterPdf(documentoId, servidorId);
  return data.pdf_base64;
}

async function downloadPdfAssinado(servidorId: number, documentoId: number, nomeArquivo: string) {
  const res = await fetch(
    `${API_URL}/documentos/download-assinado/${servidorId}/${documentoId}`,
    { headers: { Authorization: `Bearer ${getToken()}` } },
  );
  if (!res.ok) throw new Error('Erro ao baixar PDF');
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = nomeArquivo.replace(/\.pdf$/i, '_assinado.pdf');
  a.click();
  URL.revokeObjectURL(url);
}

// ─── PDF Viewer ───────────────────────────────────────────────────────────────
const PdfViewerDialog: React.FC<{
  open: boolean; doc: any; onClose: () => void;
}> = ({ open, doc, onClose }) => {
  const [pdf, setPdf]         = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open && doc) load();
    if (!open) setPdf(null);
  }, [open, doc]);

  const load = async () => {
    setLoading(true);
    try {
      const sid = doc.servidor?.id || doc.servidor_id;
      setPdf(await obterPdfBase64(doc.id, sid));
    } catch { toast.error('Erro ao carregar PDF'); }
    finally { setLoading(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const sid = doc.servidor?.id || doc.servidor_id;
      await downloadPdfAssinado(sid, doc.id, doc.nome_arquivo);
      toast.success('Download iniciado!');
    } catch { toast.error('Erro ao baixar PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{
        background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
        px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PdfIcon sx={{ color: 'rgba(255,255,255,0.85)' }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="white" sx={{ lineHeight: 1.2 }}>
              {doc?.nome_arquivo}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
              {doc?.servidor?.nome_completo}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {doc?.status === 'ASSINADO' && (
            <Button size="small" variant="contained" onClick={handleDownload} disabled={downloading}
              startIcon={downloading ? <CircularProgress size={13} color="inherit" /> : <DownloadIcon />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                color: 'white', fontWeight: 700, borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}>
              {downloading ? 'Baixando...' : 'Baixar Assinado'}
            </Button>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
      </Box>
      <DialogContent dividers sx={{ p: 0, bgcolor: '#f0f0f0', minHeight: 560 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 480, gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">Carregando documento...</Typography>
          </Box>
        ) : pdf ? (
          <iframe src={`data:application/pdf;base64,${pdf}`} width="100%" height="560"
            style={{ border: 'none', display: 'block' }} title="PDF" />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 480, gap: 2 }}>
            <PdfIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
            <Typography color="text.secondary">PDF não disponível</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={doc?.status} size="small"
            color={doc?.status === 'ASSINADO' ? 'success' : 'warning'}
            icon={doc?.status === 'ASSINADO' ? <CheckCircleIcon /> : <PendingIcon />} />
          {doc?.data_assinatura && (
            <Typography variant="caption" color="text.secondary">
              Assinado em {fmt(doc.data_assinatura)}
            </Typography>
          )}
        </Box>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const AdvogadoDocumentosPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const params    = new URLSearchParams(location.search);

  const [documentos, setDocumentos] = useState<any[]>([]);
  const [total, setTotal]           = useState(0);
  const [totais, setTotais]         = useState({ todos: 0, assinados: 0, pendentes: 0 });
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]         = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'assinados' | 'pendentes'>(
    (params.get('filtro') as any) || 'todos',
  );
  const [filtroTipo, setFiltroTipo] = useState<string>(params.get('tipo') || 'todos');
  const [viewerDoc, setViewerDoc]   = useState<any>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListarDocumentos({
        page: page + 1,
        limit: rowsPerPage,
        filtro: filtroStatus === 'todos' ? undefined : filtroStatus,
        search: search || undefined,
      });
      if (Array.isArray(res)) {
        setDocumentos(res); setTotal(res.length);
        const a = res.filter((d: any) => d.status === 'ASSINADO').length;
        const p = res.filter((d: any) => d.status === 'PENDENTE').length;
        setTotais({ todos: res.length, assinados: a, pendentes: p });
      } else {
        setDocumentos(res.data || []); setTotal(res.total || 0);
        if (res.totais) setTotais(res.totais);
      }
    } catch { toast.error('Erro ao carregar documentos'); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, filtroStatus, search]);

  useEffect(() => { load(); }, [load]);

  const filtered = filtroTipo === 'todos'
    ? documentos
    : documentos.filter(d => d.tipo === filtroTipo);

  const handleDownload = async (doc: any) => {
    setDownloading(doc.id);
    try {
      const sid = doc.servidor?.id || doc.servidor_id;
      await downloadPdfAssinado(sid, doc.id, doc.nome_arquivo);
      toast.success('Download iniciado!');
    } catch { toast.error('Erro ao baixar PDF'); }
    finally { setDownloading(null); }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6FB' }}>

      {/* Top bar */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
        px: { xs: 2, md: 4 }, py: 2,
        display: 'flex', alignItems: 'center', gap: 1.5,
        boxShadow: '0 3px 16px rgba(27,94,32,0.28)',
      }}>
        <Tooltip title="Voltar ao painel">
          <IconButton onClick={() => navigate('/admin/advogado')} size="small" sx={{ color: 'white' }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <DescriptionIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} color="white" sx={{ lineHeight: 1.1 }}>
            Documentos Assinados
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Visualize e baixe os PDFs assinados
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '13px !important', color: 'rgba(255,255,255,0.8) !important' }} />}
            label={`${totais.assinados} assinados`}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', fontWeight: 700 }} />
          <Chip size="small"
            label={`${totais.pendentes} pendentes`}
            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.18)', fontWeight: 600, display: { xs: 'none', sm: 'flex' } }} />
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          bgcolor: 'white', borderRadius: 3,
          border: '1px solid', borderColor: 'divider',
          boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}>
          {/* Filters row */}
          <Box sx={{
            px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <TextField
              placeholder="Pesquisar por servidor, CPF ou arquivo..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              size="small" sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: loading && searchInput
                  ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                  : null,
                sx: { borderRadius: 2 },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tipo</InputLabel>
              <Select value={filtroTipo} label="Tipo"
                onChange={e => { setFiltroTipo(e.target.value); setPage(0); }}
                sx={{ borderRadius: 2 }}>
                <MenuItem value="todos">Todos os tipos</MenuItem>
                <MenuItem value="CONTRATO">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GavelIcon sx={{ fontSize: 16, color: '#7B1FA2' }} /> Contrato
                  </Box>
                </MenuItem>
                <MenuItem value="SOLICITACAO">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon sx={{ fontSize: 16, color: '#2E7D32' }} /> Solicitação
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Tabs */}
          <Tabs
            value={filtroStatus}
            onChange={(_, v) => { setFiltroStatus(v); setPage(0); }}
            sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
            TabIndicatorProps={{ style: { backgroundColor: '#2E7D32' } }}
          >
            {[
              { value: 'todos',     label: `Todos (${totais.todos})` },
              { value: 'assinados', label: `Assinados (${totais.assinados})` },
              { value: 'pendentes', label: `Pendentes (${totais.pendentes})` },
            ].map(tab => (
              <Tab key={tab.value} value={tab.value} label={tab.label}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                  '&.Mui-selected': { color: '#2E7D32' } }} />
            ))}
          </Tabs>

          {loading && documentos.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
              <CircularProgress size={40} sx={{ color: '#2E7D32' }} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8F9FC' }}>
                      {['Servidor', 'Documento', 'Tipo', 'Status', 'Criado em', 'Assinado em', 'Tamanho', 'Ações'].map(h => (
                        <TableCell key={h} align={h === 'Ações' ? 'center' : 'left'}
                          sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: 0.4, py: 1.5 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    {filtered.map(doc => (
                      <TableRow key={doc.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{
                              width: 32, height: 32, fontSize: 13, fontWeight: 700,
                              bgcolor: '#E8F5E9', color: '#2E7D32',
                              border: '1.5px solid #2E7D3220',
                            }}>
                              {doc.servidor?.nome_completo?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 150 }}>
                                {doc.servidor?.nome_completo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                {doc.servidor?.cpf}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PdfIcon sx={{ color: '#E53935', fontSize: 17 }} />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{doc.nome_arquivo}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small" variant="outlined"
                            label={doc.tipo === 'CONTRATO' ? 'Contrato' : 'Solicitação'}
                            color={doc.tipo === 'CONTRATO' ? 'secondary' : 'success'}
                            icon={doc.tipo === 'CONTRATO'
                              ? <GavelIcon sx={{ fontSize: '13px !important' }} />
                              : <AssignmentIcon sx={{ fontSize: '13px !important' }} />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={doc.status}
                            color={doc.status === 'ASSINADO' ? 'success' : 'warning'}
                            icon={doc.status === 'ASSINADO'
                              ? <CheckCircleIcon sx={{ fontSize: '13px !important' }} />
                              : <PendingIcon sx={{ fontSize: '13px !important' }} />} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{fmt(doc.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{fmt(doc.data_assinatura)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{fmtSize(doc.tamanho)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Visualizar PDF">
                              <IconButton size="small" color="primary"
                                onClick={() => { setViewerDoc(doc); setViewerOpen(true); }}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {doc.status === 'ASSINADO' && (
                              <Tooltip title="Baixar PDF assinado">
                                <IconButton size="small" color="success"
                                  disabled={downloading === doc.id}
                                  onClick={() => handleDownload(doc)}>
                                  {downloading === doc.id
                                    ? <CircularProgress size={15} />
                                    : <DownloadIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                            <DescriptionIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                            <Typography color="text.secondary">Nenhum documento encontrado</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div" count={total} page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              />
            </>
          )}
        </Box>
      </Box>

      <PdfViewerDialog open={viewerOpen} doc={viewerDoc} onClose={() => setViewerOpen(false)} />
    </Box>
  );
};

export default AdvogadoDocumentosPage;